import { isNotEmpty, useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import usePolicyVerification from '@v2/commons/hooks/policyVerification/usePolicyVerification'
import { getNetChangesBetweenObjects } from '@v2/commons/utils/objectManipulation'
import { selectCurrentOrganization } from '@v2/domains/user/selectors/user.selectors'
import { useAppSelector } from '@v2/store/hooks'
import { WbfPolicies } from '@workers-benefit-fund/shared-data'

import { useUpdateTaskMutation } from '../api/task.api'
import { type MutateTaskData } from '../task.types'

type OnSubmit = (prevValues: MutateTaskData) => void

const useUpdateTask = () => {
  const { id: organizationId } = useAppSelector(selectCurrentOrganization)

  const hasUpdateTaskPolicy = usePolicyVerification(
    WbfPolicies.is_update_task_enabled,
  )

  const [updateTask, updateTaskMutationStatus] = useUpdateTaskMutation()

  const updateTaskForm = useForm<MutateTaskData>({
    mode: 'uncontrolled',
    validateInputOnBlur: true,
    validate: {
      subject: isNotEmpty('this field is required'),
    },
    transformValues: (values) => {
      const {
        person = { id: '' },
        assignedTo,
        createdBy,
        createdAt,
        updatedAt,
        updatedBy,
        deletedAt,
        completedDate,
        ...rest
      } = values
      return {
        ...rest,
      }
    },
  })

  const onSubmitForm: OnSubmit = (prevValues) => {
    updateTaskForm.onSubmit(async (values) => {
      const netChanges: Partial<MutateTaskData> = getNetChangesBetweenObjects<
        MutateTaskData,
        MutateTaskData
      >(values, prevValues)
      const query = {
        organizationId,
        id: values.id,
        ...netChanges,
      }

      return updateTask(query)
        .unwrap()
        .then(() => {
          updateTaskForm.reset()
        })
        .catch((error) => {
          notifications.show({
            title: 'Something went wrong...',
            message: error.data.error,
            color: 'red',
          })
        })
    })()
  }

  return {
    updateTaskForm,
    onSubmitForm,
    updateTaskMutationStatus,
    hasUpdateTaskPolicy,
  }
}

export default useUpdateTask
